/**
 * PDF Document Viewer - MinIO Document Viewing (TASK-282)
 * Displays PDFs stored in MinIO with navigation and metadata
 *
 * Features:
 * - Actual PDF rendering with react-pdf
 * - Page navigation controls
 * - Zoom controls
 * - Document metadata display
 * - Extracted data tab (if available)
 * - Download button
 */

import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  FileText,
  Info,
  Database,
  Loader2,
  AlertCircle,
  RotateCw,
} from 'lucide-react';
import { documentService, ClientDocument } from '../../services/documentService';

// Configure PDF.js worker — Vite resolves the ?url import to the correct asset path
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Import react-pdf styles
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

interface PDFDocumentViewerProps {
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
}

type ViewTab = 'pdf' | 'metadata' | 'extraction';

const PDFDocumentViewer: React.FC<PDFDocumentViewerProps> = ({
  documentId,
  isOpen,
  onClose,
}) => {
  // Document state
  const [document, setDocument] = useState<ClientDocument | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // PDF state
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState<ViewTab>('pdf');
  const [downloading, setDownloading] = useState(false);

  // Fetch document data
  useEffect(() => {
    if (isOpen && documentId) {
      fetchDocumentData();
    }
    return () => {
      // Cleanup PDF URL on unmount
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen, documentId]);

  const fetchDocumentData = async () => {
    try {
      setLoading(true);
      setError(null);
      setPdfLoading(true);

      // Fetch document metadata
      const docData = await documentService.getDocument(documentId);
      setDocument(docData);

      // Get presigned URL for PDF viewing
      const presigned = await documentService.getPresignedUrl(documentId, 3600);
      setPdfUrl(presigned.url);
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setError('Failed to load document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // PDF document loaded
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
    setPdfLoading(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF. The file may be corrupted or inaccessible.');
    setPdfLoading(false);
  };

  // Navigation
  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(numPages, prev + 1));
  };

  const goToPage = (page: number) => {
    const validPage = Math.min(Math.max(1, page), numPages);
    setCurrentPage(validPage);
  };

  // Zoom
  const zoomIn = () => {
    setScale((prev) => Math.min(2.5, prev + 0.25));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.25));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  // Download
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await documentService.downloadDocument(documentId);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document?.filename || 'document.pdf';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document.');
    } finally {
      setDownloading(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowLeft':
          goToPrevPage();
          break;
        case 'ArrowRight':
          goToNextPage();
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            zoomIn();
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            zoomOut();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, numPages]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-4">
            <FileText className="w-6 h-6 text-kielis-navy" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {document?.filename || 'Loading...'}
              </h2>
              <p className="text-sm text-gray-500">
                {document && (
                  <>
                    {documentService.getDocTypeDisplayName(document.doc_type)} •{' '}
                    {documentService.formatFileSize(document.file_size)}
                    {numPages > 0 && ` • ${numPages} pages`}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Download button */}
            <button
              onClick={handleDownload}
              disabled={downloading || loading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          <button
            onClick={() => setActiveTab('pdf')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pdf'
                ? 'border-kielis-navy text-kielis-navy'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4" />
            Document
          </button>
          <button
            onClick={() => setActiveTab('metadata')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'metadata'
                ? 'border-kielis-navy text-kielis-navy'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Info className="w-4 h-4" />
            Details
          </button>
          {document?.extracted_data &&
            Object.keys(document.extracted_data).length > 0 && (
              <button
                onClick={() => setActiveTab('extraction')}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'extraction'
                    ? 'border-kielis-navy text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Database className="w-4 h-4" />
                Extracted Data
              </button>
            )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-kielis-navy animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading document...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={fetchDocumentData}
                  className="flex items-center gap-2 px-4 py-2 bg-kielis-navy text-white rounded-lg hover:bg-kielis-navy/90 mx-auto"
                >
                  <RotateCw className="w-4 h-4" />
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* PDF Tab */}
              {activeTab === 'pdf' && pdfUrl && (
                <div className="h-full flex flex-col">
                  {/* PDF Controls */}
                  <div className="flex items-center justify-center gap-4 py-3 bg-white border-b border-gray-200">
                    {/* Page navigation */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={goToPrevPage}
                        disabled={currentPage <= 1}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      <span className="text-sm text-gray-600 min-w-[100px] text-center">
                        Page{' '}
                        <input
                          type="number"
                          min={1}
                          max={numPages}
                          value={currentPage}
                          onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                          className="w-12 px-2 py-1 text-center border border-gray-300 rounded"
                        />{' '}
                        of {numPages}
                      </span>

                      <button
                        onClick={goToNextPage}
                        disabled={currentPage >= numPages}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="w-px h-6 bg-gray-300" />

                    {/* Zoom controls */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={zoomOut}
                        disabled={scale <= 0.5}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ZoomOut className="w-5 h-5" />
                      </button>

                      <button
                        onClick={resetZoom}
                        className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      >
                        {Math.round(scale * 100)}%
                      </button>

                      <button
                        onClick={zoomIn}
                        disabled={scale >= 2.5}
                        className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ZoomIn className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* PDF Document */}
                  <div className="flex-1 overflow-auto flex justify-center py-6">
                    {pdfLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <Loader2 className="w-8 h-8 text-kielis-navy animate-spin" />
                      </div>
                    )}
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={null}
                      className="shadow-lg"
                    >
                      <Page
                        pageNumber={currentPage}
                        scale={scale}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        className="bg-white"
                      />
                    </Document>
                  </div>
                </div>
              )}

              {/* Metadata Tab */}
              {activeTab === 'metadata' && document && (
                <div className="h-full overflow-auto p-6">
                  <div className="max-w-3xl mx-auto space-y-6">
                    {/* Basic Info */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Document Information
                      </h3>
                      <dl className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Filename
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {document.filename}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Document Type
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {documentService.getDocTypeDisplayName(document.doc_type)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            File Size
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {documentService.formatFileSize(document.file_size)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            MIME Type
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {document.mime_type || 'Unknown'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Status
                          </dt>
                          <dd>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${documentService.getStatusColor(
                                document.status
                              )}`}
                            >
                              {document.status}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Uploaded
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {document.created_at
                              ? new Date(document.created_at).toLocaleString()
                              : 'Unknown'}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Description */}
                    {document.description && (
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Description
                        </h3>
                        <p className="text-sm text-gray-700">
                          {document.description}
                        </p>
                      </div>
                    )}

                    {/* Extraction Info */}
                    {document.extraction_confidence !== undefined && (
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Extraction Information
                        </h3>
                        <dl className="grid grid-cols-2 gap-4">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Method
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {document.extraction_method || 'Unknown'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Confidence
                            </dt>
                            <dd>
                              <span
                                className={`text-sm font-medium ${
                                  document.extraction_confidence > 0.8
                                    ? 'text-green-600'
                                    : document.extraction_confidence > 0.6
                                    ? 'text-yellow-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {Math.round(document.extraction_confidence * 100)}%
                              </span>
                            </dd>
                          </div>
                        </dl>
                      </div>
                    )}

                    {/* Tags */}
                    {document.tags && document.tags.length > 0 && (
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Tags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {document.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Extracted Data Tab */}
              {activeTab === 'extraction' &&
                document?.extracted_data &&
                Object.keys(document.extracted_data).length > 0 && (
                  <div className="h-full overflow-auto p-6">
                    <div className="max-w-4xl mx-auto">
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Extracted Data
                        </h3>
                        <pre className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg overflow-auto max-h-[60vh]">
                          {JSON.stringify(document.extracted_data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
              {document && (
                <>
                  Document ID: <code className="text-xs">{document.id}</code>
                </>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span>Use arrow keys to navigate pages</span>
              <span>Ctrl +/- to zoom</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFDocumentViewer;
